import React, { Component } from 'react';
import { connect } from 'react-redux';

import * as billboardActions from '../actions/index';
import Modal from '../../global/modal/modal';
import logger from '../../global/logger';

// #482 — per-playlist series art controls. Rendered right-aligned
// inline above the playlist's media grid, per the ticket spec:
//
//   > Each playlist should have a checkmark button at the top of the
//   > playlist editor, "Show series art" and a "Connect..." and
//   > "Upload..." button. Once connected, it should only show a
//   > "Disconnect" button in place of "Connect" and "Upload" with the
//   > name of the connected Planning Center service type name. If the
//   > art is uploaded instead, show the filename and a "Remove" button.
//
// State pulls the enable + source + connected id + uploaded filename
// off the currently `selected_playlist` (Redux), which the API returns
// from PATCH /api/v2/playlists/:id — so every operation round-trips
// through the store and there is no local mirror to drift.
export class PlaylistSeriesArtToolbar extends Component {
    _fileInput = null;

    componentDidMount() {
        // Prefetch the org's service types so the Connect modal opens
        // instantly. Cheap (just a name + id list per type) and reused
        // across every playlist in the editor, so it's fine to fire once
        // on mount even if the user never clicks Connect.
        this.props.onFetchServiceTypes();
    }

    toggleEnabled = () => {
        const playlist = this.props.playlist;
        const nextEnabled = !playlist.series_art_enabled;
        this.props.onUpdatePlaylistSeriesArt(playlist.id, { series_art_enabled: nextEnabled });
    };

    disconnect = () => {
        this.props.onUpdatePlaylistSeriesArt(this.props.playlist.id, {
            series_art_source: '',
            series_art_service_type_id: '',
        });
    };

    remove = () => {
        this.props.onUpdatePlaylistSeriesArt(this.props.playlist.id, {
            series_art_source: '',
            series_art_uploaded_url: '',
            series_art_uploaded_filename: '',
        });
    };

    openConnectModal = () => {
        const types = this.props.series_art_service_types || [];
        Modal.insertModalElement(
            <ConnectServiceTypeModal
                serviceTypes={types}
                onPick={(id) => this.pickServiceType(id)}
            />,
            true
        );
    };

    pickServiceType = (service_type_id) => {
        Modal.dismissModal();
        this.props.onUpdatePlaylistSeriesArt(this.props.playlist.id, {
            series_art_enabled: true,
            series_art_source: 'pco',
            series_art_service_type_id: service_type_id,
        });
    };

    openFilePicker = () => {
        if (this._fileInput) this._fileInput.click();
    };

    onFilePicked = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        logger.log('series-art upload: ' + file.name);
        this.props.onUploadPlaylistSeriesArt(this.props.playlist.id, file);
        event.target.value = null;
    };

    connectedServiceTypeName() {
        const id = this.props.playlist.series_art_service_type_id;
        if (!id) return null;
        const types = this.props.series_art_service_types || [];
        const match = types.find(t => t.id === id);
        return match ? match.name : null;
    }

    render() {
        const playlist = this.props.playlist;
        if (!playlist) return null;

        const busy = this.props.updating_playlist_series_art || this.props.uploading_playlist_series_art;
        const enabled = !!playlist.series_art_enabled;
        const source = playlist.series_art_source;
        const uploadedFilename = playlist.series_art_uploaded_filename;
        const connectedName = this.connectedServiceTypeName();

        return (
            <div
                id="playlist_series_art_toolbar"
                className="flex items-center justify-end gap-3 mb-3 text-sm text-gray-700 flex-wrap"
            >
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <input
                        id="playlist_series_art_toggle"
                        type="checkbox"
                        className="h-4 w-4 accent-blue-500"
                        checked={enabled}
                        disabled={busy}
                        onChange={this.toggleEnabled}
                    />
                    <span>Show series art</span>
                </label>

                {source === 'pco' && (
                    <div className="inline-flex items-center gap-2">
                        {connectedName && (
                            <span
                                id="playlist_series_art_connected_name"
                                className="text-gray-500 truncate max-w-[200px]"
                                title={connectedName}
                            >
                                {connectedName}
                            </span>
                        )}
                        <button
                            id="playlist_series_art_disconnect_button"
                            type="button"
                            className="inline-flex items-center rounded-sm border border-gray-300 bg-white px-3 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            onClick={this.disconnect}
                            disabled={busy}
                        >
                            Disconnect
                        </button>
                    </div>
                )}

                {source === 'upload' && (
                    <div className="inline-flex items-center gap-2">
                        {uploadedFilename && (
                            <span
                                id="playlist_series_art_uploaded_filename"
                                className="text-gray-500 truncate max-w-[200px]"
                                title={uploadedFilename}
                            >
                                {uploadedFilename}
                            </span>
                        )}
                        <button
                            id="playlist_series_art_remove_button"
                            type="button"
                            className="inline-flex items-center rounded-sm border border-gray-300 bg-white px-3 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            onClick={this.remove}
                            disabled={busy}
                        >
                            Remove
                        </button>
                    </div>
                )}

                {!source && (
                    <div className="inline-flex items-center gap-2">
                        <button
                            id="playlist_series_art_connect_button"
                            type="button"
                            className="inline-flex items-center rounded-sm border border-blue-300 bg-white px-3 py-1 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                            onClick={this.openConnectModal}
                            disabled={busy}
                        >
                            Connect...
                        </button>
                        <button
                            id="playlist_series_art_upload_button"
                            type="button"
                            className="inline-flex items-center rounded-sm border border-blue-300 bg-white px-3 py-1 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                            onClick={this.openFilePicker}
                            disabled={busy}
                        >
                            {this.props.uploading_playlist_series_art ? 'Uploading...' : 'Upload...'}
                        </button>
                        <input
                            ref={(el) => { this._fileInput = el; }}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={this.onFilePicked}
                        />
                    </div>
                )}
            </div>
        );
    }
}

// Simple picker modal for the Connect... flow. Reused inline here
// because it's tiny and only ever mounted from this toolbar.
export class ConnectServiceTypeModal extends Component {
    render() {
        const types = this.props.serviceTypes || [];
        return (
            <div
                id="playlist_series_art_connect_modal"
                className="bg-white rounded-md shadow-lg p-6 max-w-md mx-auto"
            >
                <h3 className="text-lg font-bold mb-3">Connect a service type</h3>
                <p className="text-sm text-gray-600 mb-4">
                    Pick the Planning Center service type whose current or
                    upcoming plan should provide this playlist's series art.
                </p>
                {types.length === 0 ? (
                    <div className="text-sm text-gray-500">
                        No Planning Center service types are connected to this
                        organization yet. Complete a PCO sync first.
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-200 max-h-72 overflow-y-auto">
                        {types.map((type) => (
                            <li key={type.id}>
                                <button
                                    type="button"
                                    className="w-full text-left px-2 py-3 hover:bg-blue-50 rounded-sm"
                                    onClick={() => this.props.onPick(type.id)}
                                >
                                    {type.name}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        series_art_service_types: state.billboard.series_art_service_types,
        loading_series_art_service_types: state.billboard.loading_series_art_service_types,
        updating_playlist_series_art: state.billboard.updating_playlist_series_art,
        uploading_playlist_series_art: state.billboard.uploading_playlist_series_art,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        onFetchServiceTypes: () => dispatch(billboardActions.fetchSeriesArtServiceTypes()),
        onUpdatePlaylistSeriesArt: (playlist_id, attrs, callback) =>
            dispatch(billboardActions.updatePlaylistSeriesArt(playlist_id, attrs, callback)),
        onUploadPlaylistSeriesArt: (playlist_id, file, callback) =>
            dispatch(billboardActions.uploadPlaylistSeriesArt(playlist_id, file, callback)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PlaylistSeriesArtToolbar);
